import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, register } from 'timeago.js';

type SupportedLocale = 'ko' | 'ja';

const localeLoaders: Record<SupportedLocale, () => Promise<any>> = {
  ko: () => import('timeago.js/lib/lang/ko'),
  ja: () => import('timeago.js/lib/lang/ja'),
};

const loadedLocales = new Set<string>();

async function loadLocale(locale: SupportedLocale) {
  if (loadedLocales.has(locale)) return;
  const { default: localeFunc } = await localeLoaders[locale]();
  register(locale, localeFunc);
  loadedLocales.add(locale);
}

function isSupportedLocale(lang: string): lang is SupportedLocale {
  return lang in localeLoaders;
}

export function useTimeAgoLocale(): string {
  const { i18n } = useTranslation();
  const [localeReady, setLocaleReady] = useState(
    !isSupportedLocale(i18n.language) || loadedLocales.has(i18n.language)
  );

  useEffect(() => {
    const lang = i18n.language;
    if (!isSupportedLocale(lang)) {
      setLocaleReady(true);
      return;
    }
    if (loadedLocales.has(lang)) {
      setLocaleReady(true);
      return;
    }
    setLocaleReady(false);
    loadLocale(lang).then(() => setLocaleReady(true));
  }, [i18n.language]);

  if (localeReady && isSupportedLocale(i18n.language)) {
    return i18n.language;
  }
  return 'en';
}

export function formatTimeAgo(date: string, locale: string): string {
  return format(date, locale);
}
